import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import InViewObserver from 'components/InViewObserver';
import {
  addLoadableComponent,
  updateRequestIdleCallBackStatus,
} from 'reduxUtils/modules/loadableManager';
import { getLoadableComponent } from '../../../utils/componentLoader';
import LoaderSpinner from '../../common/LoaderSpinner';
import s from './LoadableComponentLoader.scss';

const LoadableComponentLoader = props => {
  const {
    loadableComponentName,
    loadOnScroll,
    loadOnScrollFlag,
    loadOnClick,
    loadOnClickFlag,
    loadOnDocLoad,
    loadOnDocLoadFlag,
    fallbackComponent,
    loadInView,
    viewMargin,
    loadableComponents,
    setLoadableComponent,
    requestIdleCallbackStatus,
    setUpdateRequestIdleCallBackStatus,
    placeHolderStyles,
    placeHolderLoaderClass,
    loadableClass,
    isSmallSize,
    noBackground,
  } = props;

  const [loadInViewFlag, setLoadInViewFlag] = useState(false);
  const [hideComponentOnError, setHideComponentOnError] = useState(false);

  if (!loadableComponentName) {
    return null;
  }

  const callHideComponentOnError = () => {
    setHideComponentOnError(true);
  };

  useEffect(() => {
    if (
      !loadableComponents[loadableComponentName] &&
      !requestIdleCallbackStatus[loadableComponentName]
    ) {
      setUpdateRequestIdleCallBackStatus({
        [loadableComponentName]: 'registered',
      }); // so that requestIdleCallback will not called multiple times for component
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => {
          const comp = getLoadableComponent(loadableComponentName);
          setLoadableComponent({ [loadableComponentName]: comp });
        });
      } else {
        setTimeout(() => {
          const comp = getLoadableComponent(loadableComponentName);
          setLoadableComponent({ [loadableComponentName]: comp });
        }, 0);
      }
    }
  }, []);

  const LoadableComponent = loadableComponents[loadableComponentName];

  let renderComponent = false;
  if (
    (loadOnScroll && loadOnScrollFlag) ||
    (loadOnClick && loadOnClickFlag) ||
    (loadOnDocLoad && loadOnDocLoadFlag) ||
    (loadInView && loadInViewFlag)
  ) {
    renderComponent = true;
  }

  if (!(loadOnScroll || loadOnClick || loadOnDocLoad || loadInView)) {
    renderComponent = true;
  }
  // let fallback = fallbackComponent || null;

  let fallback = placeHolderStyles
    ? fallbackComponent || (
        <LoaderSpinner
          overwriteLoader="loadeblecomponentloader"
          overwriteWrapper={placeHolderLoaderClass}
        />
      )
    : fallbackComponent;

  if (loadInView) {
    fallback = (
      <InViewObserver
        inViewCallback={() => {
          setLoadInViewFlag(true);
        }}
        viewMargin={viewMargin}
        className="loadable-inview"
      >
        {fallback}
      </InViewObserver>
    );
  }

  const renderComponentJSX =
    renderComponent && loadableComponents[loadableComponentName] ? (
      <LoadableComponent
        {...props}
        callHideComponentOnError={callHideComponentOnError}
      />
    ) : (
      fallback
    );

  if (hideComponentOnError) {
    return <></>;
  }
  if (placeHolderStyles) {
    return (
      <div
        className={`${s.loadebleComponent} ${loadableClass} 
        ${isSmallSize ? s.smallSize : ''}
        ${noBackground ? s.noBg : ''}`}
        style={placeHolderStyles}
      >
        {renderComponentJSX}
      </div>
    );
  }
  return renderComponentJSX;
};

const mapStateToProps = state => ({
  loadOnScrollFlag: state?.loadableManager?.loadLoadableOnScroll || false,
  loadOnClickFlag: state?.loadableManager?.loadLoadableOnClick || false,
  loadOnDocLoadFlag: state?.loadableManager?.loadLoadableOnLoad || false,
  loadableComponents: state?.loadableManager?.loadableComponents || {},
  requestIdleCallbackStatus:
    state?.loadableManager?.requestIdleCallbackStatus || {},
});

const mapDispatchToProps = dispatch => ({
  setLoadableComponent: params => {
    dispatch(addLoadableComponent(params));
  },
  setUpdateRequestIdleCallBackStatus: params => {
    dispatch(updateRequestIdleCallBackStatus(params));
  },
});
LoadableComponentLoader.propTypes = {
  loadOnScroll: PropTypes.bool,
  loadOnScrollFlag: PropTypes.bool,
  loadOnClick: PropTypes.bool,
  loadOnClickFlag: PropTypes.bool,
  loadOnDocLoad: PropTypes.bool,
  loadOnDocLoadFlag: PropTypes.bool,
  loadableComponentName: PropTypes.string,
  loadInView: PropTypes.bool,
  viewMargin: PropTypes.string,
  fallbackComponent: PropTypes.node,
  loadableComponents: PropTypes.shape({}),
  setLoadableComponent: PropTypes.func,
  requestIdleCallbackStatus: PropTypes.shape({}),
  setUpdateRequestIdleCallBackStatus: PropTypes.func,
  placeHolderStyles: PropTypes.shape({
    minHeight: PropTypes.string,
  }),
  placeHolderLoaderClass: PropTypes.string,
  loadableClass: PropTypes.string,
  isSmallSize: PropTypes.bool,
  noBackground: PropTypes.bool,
};

LoadableComponentLoader.defaultProps = {
  loadOnScroll: false,
  loadOnScrollFlag: false,
  loadOnClick: false,
  loadOnClickFlag: false,
  loadOnDocLoad: false,
  loadOnDocLoadFlag: false,
  loadableComponentName: '',
  loadInView: false,
  viewMargin: '300px 0px 300px 0px',
  fallbackComponent: null,
  loadableComponents: {},
  setLoadableComponent: () => {},
  requestIdleCallbackStatus: {},
  setUpdateRequestIdleCallBackStatus: () => {},
  placeHolderStyles: null,
  placeHolderLoaderClass: null,
  loadableClass: '',
  isSmallSize: '',
  noBackground: '',
};

export default connect(mapStateToProps, mapDispatchToProps)(
  LoadableComponentLoader,
);
